import { ITEMS } from '@/data/items';
import { NPCS } from '@/data/npcs';
import type { Inventory } from '@/world/inventory';
import type { PostedRequest, RequestBoard } from '@/world/requests';

/**
 * The board, read.
 *
 * Three notices, each showing who asked, for what, how much of it you are
 * carrying, and what it pays. The count is the whole interface: "4 / 7" says
 * both what is wanted and how far off you are, which is the only question
 * anyone asks at a notice board.
 *
 * Reuses the cart's panel styling. Two panels that list rows and a price
 * should look like two panels that list rows and a price.
 */

export interface RequestBoardPanelOptions {
  readonly container: HTMLElement;
  readonly board: RequestBoard;
  readonly inventory: Inventory;
  readonly dayIndex: () => number;
  readonly onFill: (request: PostedRequest) => void;
}

export class RequestBoardPanel {
  private readonly root: HTMLDivElement;
  private readonly panel: HTMLDivElement;
  private readonly body: HTMLDivElement;

  constructor(private readonly options: RequestBoardPanelOptions) {
    this.root = document.createElement('div');
    this.root.className = 'shop-root';

    this.panel = document.createElement('div');
    this.panel.className = 'shop-panel';
    this.panel.hidden = true;
    this.panel.setAttribute('role', 'dialog');
    this.panel.setAttribute('aria-label', 'Notice board');

    const header = document.createElement('div');
    header.className = 'shop-header';

    const title = document.createElement('span');
    title.className = 'shop-title';
    title.textContent = 'Notice Board';

    const hint = document.createElement('span');
    hint.className = 'shop-purse';
    hint.textContent = 'Taken down at dusk';

    header.append(title, hint);

    this.body = document.createElement('div');
    this.body.className = 'shop-body board-body';

    const done = document.createElement('button');
    done.className = 'gear-done';
    done.type = 'button';
    done.textContent = 'Done';
    done.addEventListener('click', () => {
      this.setOpen(false);
    });

    this.panel.append(header, this.body, done);
    this.root.appendChild(this.panel);
    options.container.appendChild(this.root);
  }

  get isOpen(): boolean {
    return !this.panel.hidden;
  }

  setOpen(open: boolean): void {
    this.panel.hidden = !open;
    document.body.classList.toggle('menu-open', open);
    if (open) this.refresh();
  }

  refresh(): void {
    const { board, dayIndex } = this.options;
    const day = dayIndex();

    this.body.replaceChildren(
      ...board.postings(day).map((request) => this.notice(request, board.isFilled(request.id, day))),
    );
  }

  destroy(): void {
    this.root.remove();
  }

  private notice(request: PostedRequest, filled: boolean): HTMLElement {
    const { inventory, onFill } = this.options;
    const have = inventory.countOf(request.item);
    const enough = have >= request.count;

    const notice = document.createElement('div');
    notice.className = 'board-notice';
    notice.style.setProperty('--tint', ITEMS[request.item].tint);

    const from = document.createElement('p');
    from.className = 'board-from';
    from.textContent = `${NPCS[request.from].name} asks for`;

    const what = document.createElement('p');
    what.className = 'board-what';
    what.textContent = `${request.count} × ${ITEMS[request.item].name}`;

    const note = document.createElement('p');
    note.className = 'board-note';
    note.textContent = request.note;

    const action = document.createElement('button');
    action.className = 'shop-row';
    action.type = 'button';
    action.disabled = filled || !enough;

    const carrying = document.createElement('span');
    // Filled notices keep their count rather than going blank: a board that
    // erases what you did reads like it never happened.
    carrying.textContent = filled ? 'Filled' : `${have} / ${request.count}`;

    const pay = document.createElement('span');
    pay.className = 'shop-price';
    pay.textContent = `${request.pay}c`;

    action.append(carrying, pay);
    action.addEventListener('click', () => {
      onFill(request);
      this.refresh();
    });

    notice.append(from, what, note, action);
    return notice;
  }
}
